import clsx from 'clsx';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { type IconDefinition, renderIcon } from '@gitbook/spine-react';

import { Pressable } from '../Pressable';
import styles from './SearchResultItem.module.css';
import { SearchTextHighlight } from './SearchTextHighlight';

export interface SearchResultInfo {
    icon: IconDefinition;
    title: string;
    action: string;
    subtitle?: string;
    body?: string;
    nested?: boolean;
    disabled?: boolean;
    standout?: boolean;
    showResultsAsPills?: boolean;
}

export const SearchResultItem = React.forwardRef(function SearchResultItemWithRef(
    props: SearchResultInfo & {
        active: boolean;
        onSelect: () => void;
        onHover: () => void;
        onFocus: () => void;
    },
    ref: React.Ref<HTMLDivElement>
): React.ReactElement {
    const {
        title,
        subtitle,
        body,
        icon: IconCo,
        active,
        disabled = false,
        nested = false,
        action,
        standout = false,
        onHover,
        onSelect,
        onFocus,
        showResultsAsPills,
    } = props;

    if (showResultsAsPills) {
        return (
            <Pressable
                ref={ref}
                style={clsx(
                    'max-w-full flex-start justify-start overflow-hidden rounded-full',
                    styles.suggestedQueryOrPrompt,
                    {
                        [styles.suggestedQueryOrPromptActive]: active,
                    }
                )}
                onMouseEnter={onHover}
                onFocus={onFocus}
                onPress={onSelect}
            >
                {renderIcon(IconCo, { size: 16, className: styles.icon })}
                <span
                    className={clsx(
                        'overflow-hidden text-ellipsis text-left text-ui-small',
                        styles.promptText
                    )}
                >
                    {title}
                </span>
            </Pressable>
        );
    }

    return (
        <Pressable
            ref={ref}
            style={clsx('rounded', styles.searchResultItem, {
                [styles.searchResultItemActive]: active,
                [styles.searchResultItemNested]: nested,
                [styles.searchResultItemDisabled]: disabled,
                [styles.searchResultItemStandout]: standout,
                // If the current result has no title, it means it's nested under a result
                // that does have a title and thus under the same heading and should be indented further.
                [styles.searchResultWithoutTitle]: !title,
            })}
            onMouseEnter={onHover}
            onFocus={onFocus}
            onPress={onSelect}
        >
            {renderIcon(IconCo, { size: 20, className: 'text-muted' })}
            <div className={clsx('flex flex-1 flex-col', styles.searchResultItemText)}>
                {title ? (
                    <div className={clsx('text-ui-action', styles.searchResultTitle)}>
                        <SearchTextHighlight value={title} standout={standout} />
                    </div>
                ) : null}
                {subtitle ? (
                    <div className="text-muted text-ui-small">
                        <SearchTextHighlight value={subtitle} />
                    </div>
                ) : null}
                {body ? (
                    <div className={clsx(styles.searchResultBody, 'text-base text-ui-small')}>
                        <SearchTextHighlight value={body} maxLength={280} />
                    </div>
                ) : null}
            </div>
            {active ? (
                <div className={styles.searchResultAction}>
                    <Icon.ArrowEnter className={styles.searchResultActionIcon} />
                    <span className={clsx('text-ui-action', styles.searchResultActionText)}>
                        {action}
                    </span>
                </div>
            ) : null}
        </Pressable>
    );
});
